// CollaborationWorkspacePage.tsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Tabs, Loader, Center, Text, Button, Flex, Tooltip, useMantineTheme, Alert } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import type { CSSProperties } from "react";
import TitleComponent from "@/components/shared/TitleComponent";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import SharedDocumentsTab from "./SharedDocumentsTab";
import MatchedProfileView from "@/pages/profile/MatchedView";
import QuestionnaireTab from "./QuestionnaireTab";
import { PartnershipHandshakeIcon } from "@/components/icons/PartnershipHandshakeIcon";
import {
  IconArrowLeft,
  IconBuilding,
  IconDownload,
  IconFileText,
  IconInfoCircle,
  IconMessage,
} from "@tabler/icons-react";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import CollaborationChatTab from "./CollaborationChatTab";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  NAV_TAB_LABEL_FZ,
  navTabsListStyles,
} from "@/shared/styles/navTabsLayout";
import { generateCollaborationAlignmentReport } from "@/utils/pdfGenerator";
import { gradeFullQuestionnaire } from "@/utils/gradingEngine";
import { getQuestionnaireId } from "@/utils/questionnaireMapping";
import type { FullGradingResult, Answer } from "@/utils/gradingEngine.types";
import q1 from "@/data/questionnaires/questionnaire_1.json";
import q2 from "@/data/questionnaires/questionnaire_2.json";
import q3 from "@/data/questionnaires/questionnaire_3.json";
import q4 from "@/data/questionnaires/questionnaire_4.json";
import q5 from "@/data/questionnaires/questionnaire_5.json";
import q6 from "@/data/questionnaires/questionnaire_6.json";

const CFC_TAB_TOOLTIP =
  "Partner Readiness is a short questionnaire designed to better understand your expectations for this collaboration. Based on your responses, it evaluates compatibility with your partner, provides a score, and highlights areas where you align as well as topics that may need further discussion.";

const QUESTIONNAIRE_MAP: Record<string, any> = {
  [q1.id]: q1,
  [q2.id]: q2,
  [q3.id]: q3,
  [q4.id]: q4,
  [q5.id]: q5,
  [q6.id]: q6,
};

const WORKSPACE_TABS = new Set(["overview", "documents", "questionnaire", "chat"]);

function tabFromSearchParams(searchParams: URLSearchParams): string | null {
  const t = searchParams.get("tab");
  if (t === "nda") return "documents";
  return t && WORKSPACE_TABS.has(t) ? t : null;
}

/** Tabler / custom SVG size in mobile-only tab strip (icons are hidden on `sm+`). */
const COLLAB_MOBILE_TAB_ICON = 28;

export default function CollaborationWorkspacePage() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userDetails, hasProfile } = useUserContext();
  const theme = useMantineTheme();
  /** Matches `hiddenFrom="sm"` / mobile icon strip (below Mantine `sm`). Nested `@media` in `styles.list` is unreliable here. */
  const isWorkspaceTabsMobile = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true }
  );

  const collaborationWorkspaceTabsListStyles = useMemo(
    () => ({
      list: isWorkspaceTabsMobile
        ? {
            display: "grid" as const,
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            alignItems: "stretch" as const,
            boxShadow: "none" as const,
            gap: 0,
            flexShrink: 0,
          }
        : {
            ...navTabsListStyles(4, { flexShrink: 0 }).list,
          },
    }),
    [isWorkspaceTabsMobile]
  );

  const collaborationWorkspaceTabStyles = useMemo(
    () => ({
      tab: {
        display: "flex" as const,
        alignItems: "center" as const,
        ...(isWorkspaceTabsMobile
          ? {
              flexDirection: "column" as const,
              justifyContent: "center" as const,
              alignItems: "center" as const,
              alignSelf: "stretch" as const,
              minHeight: 56,
              paddingTop: "var(--mantine-spacing-xs)",
              paddingBottom: "var(--mantine-spacing-xs)",
            }
          : {}),
      },
      tabLabel: {
        flex: 1,
        textAlign: "center" as const,
      },
      tabSection: {
        marginInlineStart: 0,
      },
    }),
    [isWorkspaceTabsMobile]
  );

  const initialTab = tabFromSearchParams(searchParams) ?? "overview";
  const [activeTab, setActiveTab] = useState<string | null>(initialTab);

  const autoOpenQuestionnaireModal = useMemo(
    () => searchParams.get("tab") === "questionnaire",
    [searchParams]
  );

  const handleWorkspaceTabChange = useCallback(
    (value: string | null) => {
      setActiveTab(value);
      if (value === "questionnaire") {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", "questionnaire");
            return next;
          },
          { replace: true }
        );
      } else {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("tab");
            return next;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams]
  );

  // Loading states
  const [loadingCollaboration, setLoadingCollaboration] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(false);

  // Data states
  const [collaboration, setCollaboration] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [questionnaireMySubmitted, setQuestionnaireMySubmitted] = useState<boolean | null>(null);

  // Derived state
  const otherCompanyId =
    collaboration?.requesterCompanyId === userDetails?.company?.id
      ? collaboration?.receiverCompanyId
      : collaboration?.requesterCompanyId;

  // 1. Fetch Collaboration (Always needed for context)
  const fetchCollaboration = useCallback(async () => {
    if (!hasProfile || !id) return;
    setLoadingCollaboration(true);
    try {
      const collabData = await trpc.collaboration.getById.query({ matchId: id });
      setCollaboration(collabData);
    } catch (err) {
      console.error("Error fetching collaboration:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCollaboration(false);
    }
  }, [id, hasProfile]);

  // 2. Fetch Company (Lazy load)
  const fetchCompany = useCallback(async (companyId: string) => {
    setLoadingCompany(true);
    try {
      const companyData = await trpc.company.getById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      console.error("Error fetching company:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  useEffect(() => {
    setQuestionnaireMySubmitted(null);
    const t = tabFromSearchParams(searchParams);
    setActiveTab(t ?? "overview");
  }, [id]);

  useEffect(() => {
    if (searchParams.get("tab") !== "nda") return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const collaborationHasQuestionnaire = useMemo(() => {
    try {
      if (!collaboration?.requesterRole || !collaboration?.expectedPartnerRole) return false;
      getQuestionnaireId(collaboration.requesterRole, collaboration.expectedPartnerRole);
      return true;
    } catch {
      return false;
    }
  }, [collaboration?.requesterRole, collaboration?.expectedPartnerRole]);

  const fetchQuestionnaireMySubmission = useCallback(async () => {
    if (!id) return;
    try {
      const data = await trpc.collaboration.getQuestionnaireResponses.query({ matchId: id });
      setQuestionnaireMySubmitted(!!data.mySubmittedAt);
    } catch {
      setQuestionnaireMySubmitted(null);
    }
  }, [id]);

  useEffect(() => {
    if (!collaboration || !id || activeTab !== "overview") return;
    fetchQuestionnaireMySubmission();
  }, [collaboration, id, activeTab, fetchQuestionnaireMySubmission]);

  const handleGoToQuestionnaireTab = useCallback(() => {
    setActiveTab("questionnaire");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "questionnaire");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Effect: Fetch Collaboration on mount
  useEffect(() => {
    fetchCollaboration();
  }, [fetchCollaboration]);

  useEffect(() => {
    if (activeTab === "overview" && otherCompanyId && !company && !loadingCompany) {
      fetchCompany(otherCompanyId);
    }
  }, [activeTab, otherCompanyId, company, loadingCompany, fetchCompany]);

  if (loadingCollaboration) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Loader size="lg" />
        </Center>
      </Box>
    );
  }

  if (!collaboration) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Text c="slate.6">Collaboration not found</Text>
        </Center>
      </Box>
    );
  }

  const handleDownloadProfile = async () => {
    setDownloadingPDF(true);
    try {
      // Fetch Party A (own company)
      const partyACompany = await trpc.company.getById.query({ companyId: userDetails!.company!.id });

      // Fetch Party B (other company), reuse if already loaded
      let partyBCompany = company;
      if (!partyBCompany && otherCompanyId) {
        partyBCompany = await trpc.company.getById.query({ companyId: otherCompanyId });
        setCompany(partyBCompany);
      }

      if (!partyACompany || !partyBCompany) return;

      // Fetch shared documents
      const sharedDocs = await trpc.collaboration.listDocuments.query({ matchId: id });

      const collaborationType =
        collaboration?.requesterRole && collaboration?.expectedPartnerRole
          ? `${collaboration.requesterRole} / ${collaboration.expectedPartnerRole}`
          : undefined;

      // Resolve grading result and section titles for section 7
      let gradingResult: FullGradingResult | null = null;
      let sectionTitleMap: Record<string, string> = {};
      let myAnswersForPdf: Record<string, any> | undefined;
      let partnerAnswersForPdf: Record<string, any> | undefined;
      let questionOptionMap: Record<string, { type: string; options: Array<{ id: string; label: string }> }> | undefined;

      if (collaboration?.requesterRole && collaboration?.expectedPartnerRole) {
        try {
          const questionnaireId = getQuestionnaireId(
            collaboration.requesterRole,
            collaboration.expectedPartnerRole
          );
          const questionnaire = QUESTIONNAIRE_MAP[questionnaireId];

          if (questionnaire) {
            // Build section title map and question option map
            questionOptionMap = {};
            for (const section of questionnaire.sections ?? []) {
              sectionTitleMap[section.id] = section.title;
              for (const q of section.questions) {
                questionOptionMap[q.id] = { type: q.type, options: q.options.map((o: any) => ({ id: o.id, label: o.label })) };
              }
            }

            // Fetch questionnaire responses
            const responseData = await trpc.collaboration.getQuestionnaireResponses.query({ matchId: id });

            if (responseData.bothSubmitted && responseData.myAnswers && responseData.partnerAnswers) {
              myAnswersForPdf = responseData.myAnswers as Record<string, any>;
              partnerAnswersForPdf = responseData.partnerAnswers as Record<string, any>;

              // Use cached grading result from server if available
              if (responseData.gradingResult) {
                gradingResult = responseData.gradingResult as unknown as FullGradingResult;
              } else {
                const isRequester = collaboration.requesterCompanyId === userDetails?.company?.id;
                const answersA = isRequester
                  ? (responseData.myAnswers as unknown as Record<string, Answer>)
                  : (responseData.partnerAnswers as unknown as Record<string, Answer>);
                const answersB = isRequester
                  ? (responseData.partnerAnswers as unknown as Record<string, Answer>)
                  : (responseData.myAnswers as unknown as Record<string, Answer>);
                gradingResult = gradeFullQuestionnaire(questionnaire, answersA, answersB);
              }
            }
          }
        } catch (err) {
          console.error("Failed to compute grading for PDF:", err);
        }
      }

      await generateCollaborationAlignmentReport({
        partyA: partyACompany,
        partyB: partyBCompany,
        sharedDocuments: sharedDocs,
        matchId: id,
        collaborationType,
        requesterRole: collaboration?.requesterRole,
        expectedPartnerRole: collaboration?.expectedPartnerRole,
        gradingResult,
        sectionTitleMap,
        myAnswers: myAnswersForPdf,
        partnerAnswers: partnerAnswersForPdf,
        questionOptionMap,
      });
    } catch (error) {
      console.error("Failed to generate report:", error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const tabIconColor = (tab: string): CSSProperties => ({
    color: activeTab === tab ? "#ffffff" : "var(--mantine-color-slate-6)",
  });

  const interestTabStyle = (tab: string) => ({
    backgroundColor: activeTab === tab ? "#04305c" : "#ebf0ff",
    color: activeTab === tab ? "white" : "var(--mantine-color-slate-6)",
    borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
    border: activeTab === tab ? "1px solid #04305c" : "1px solid #E5E7EB",
    borderBottom: "none",
    whiteSpace: "nowrap" as const,
    fontWeight: activeTab === tab ? 500 : 400,
    cursor: "pointer" as const,
    /** Match mobile strip row height so grid rows do not leave a square gap above shorter tabs (clips the look of the top-left radius on the first tab). */
    minHeight: isWorkspaceTabsMobile ? 56 : 44,
  });

  const tabRightSpacer = (
    <Box
      w={28}
      h={28}
      miw={28}
      mih={28}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
    />
  );

  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header with Back Button and Title */}
      <Flex align="center" gap="md" px="md" py="md" style={{ borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}>
        <Button
          variant="outline"
          color="gray"
          p={8}
          onClick={() => navigate("/collaborations")}
          styles={{
            root: {
              borderColor: "#E5E7EB",
              borderRadius: 8,
              minWidth: "auto",
              height: "auto",
            },
          }}
        >
          <IconArrowLeft size={18} color="#374151" />
        </Button>
        <Text fw={500} fz="xl">
          Collaboration Workspace
        </Text>
        <Button
          ml="auto"
          variant="outline"
          leftSection={<IconDownload size={18} />}
          loading={downloadingPDF}
          onClick={handleDownloadProfile}
        >
          Download Profile
        </Button>
      </Flex>

      <Box px="md" pt="md" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {collaboration.status === CollaborationStatus.BOTH_PAID && collaboration.collaborationReadOnly ? (
          <Alert color="gray" title="Collaboration closed" mb="md" variant="light">
            The active period ended 45 days after both parties paid. You can still view this workspace, chat history, and
            documents. Sending messages, uploading files, updating the questionnaire, and signing the NDA are no longer
            available.
          </Alert>
        ) : null}
        {/* Tabs — avoid overflow:hidden on ancestors so tab radii are not clipped; strip uses same horizontal padding as content (parent px="md"). */}
        <Tabs
          value={activeTab}
          onChange={handleWorkspaceTabChange}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <Box
            w="100%"
            style={{ ...NAV_TABS_FULL_WIDTH_LINE_STYLE, overflow: "visible" }}
          >
            <Tabs.List grow={!!isWorkspaceTabsMobile} styles={collaborationWorkspaceTabsListStyles}>
            <Tabs.Tab
              value="overview"
              aria-label="Overview"
              px={{ base: 4, sm: "lg" }}
              py="xs"
              fz={NAV_TAB_LABEL_FZ}
              style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("overview") }}
              styles={collaborationWorkspaceTabStyles}
              leftSection={
                <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IconBuilding size={COLLAB_MOBILE_TAB_ICON} stroke={1.5} style={tabIconColor("overview")} />
                </Box>
              }
              rightSection={<Box visibleFrom="sm">{tabRightSpacer}</Box>}
            >
              <Text
                component="span"
                fz={NAV_TAB_LABEL_FZ}
                visibleFrom="sm"
                c={activeTab === "overview" ? "#ffffff" : "var(--mantine-color-slate-6)"}
                fw={activeTab === "overview" ? 500 : 400}
              >
                Overview
              </Text>
            </Tabs.Tab>
            <Tabs.Tab
              value="questionnaire"
              aria-label="Partner Readiness"
              px={{ base: 4, sm: "lg" }}
              py="xs"
              fz={NAV_TAB_LABEL_FZ}
              style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("questionnaire") }}
              styles={collaborationWorkspaceTabStyles}
              leftSection={
                <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <PartnershipHandshakeIcon size={COLLAB_MOBILE_TAB_ICON} style={tabIconColor("questionnaire")} />
                </Box>
              }
              rightSection={
                <Box
                  visibleFrom="sm"
                  w={28}
                  h={28}
                  miw={28}
                  mih={28}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Tooltip label={CFC_TAB_TOOLTIP} multiline maw={360} withArrow position="top">
                    <Box
                      component="span"
                      role="img"
                      aria-label="What is Partner Readiness?"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ display: "inline-flex", alignItems: "center" }}
                    >
                      <IconInfoCircle
                        size={16}
                        stroke={1.5}
                        style={{
                          color: activeTab === "questionnaire" ? "#ffffff" : "var(--mantine-color-slate-6)",
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              }
            >
              <Text
                component="span"
                fz={NAV_TAB_LABEL_FZ}
                visibleFrom="sm"
                c={activeTab === "questionnaire" ? "#ffffff" : "var(--mantine-color-slate-6)"}
                fw={activeTab === "questionnaire" ? 500 : 400}
              >
                Partner Readiness
              </Text>
            </Tabs.Tab>
            <Tabs.Tab
              value="documents"
              aria-label="Shared Documents"
              px={{ base: 4, sm: "lg" }}
              py="xs"
              fz={NAV_TAB_LABEL_FZ}
              style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("documents") }}
              styles={collaborationWorkspaceTabStyles}
              leftSection={
                <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IconFileText size={COLLAB_MOBILE_TAB_ICON} stroke={1.5} style={tabIconColor("documents")} />
                </Box>
              }
              rightSection={<Box visibleFrom="sm">{tabRightSpacer}</Box>}
            >
              <Text
                component="span"
                fz={NAV_TAB_LABEL_FZ}
                visibleFrom="sm"
                c={activeTab === "documents" ? "#ffffff" : "var(--mantine-color-slate-6)"}
                fw={activeTab === "documents" ? 500 : 400}
              >
                Shared Documents
              </Text>
            </Tabs.Tab>
            <Tabs.Tab
              value="chat"
              aria-label="Chat"
              px={{ base: 4, sm: "lg" }}
              py="xs"
              fz={NAV_TAB_LABEL_FZ}
              style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("chat") }}
              styles={collaborationWorkspaceTabStyles}
              leftSection={
                <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IconMessage size={COLLAB_MOBILE_TAB_ICON} stroke={1.5} style={tabIconColor("chat")} />
                </Box>
              }
              rightSection={<Box visibleFrom="sm">{tabRightSpacer}</Box>}
            >
              <Text
                component="span"
                fz={NAV_TAB_LABEL_FZ}
                visibleFrom="sm"
                c={activeTab === "chat" ? "#ffffff" : "var(--mantine-color-slate-6)"}
                fw={activeTab === "chat" ? 500 : 400}
              >
                Chat
              </Text>
            </Tabs.Tab>
          </Tabs.List>
          </Box>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {loadingCompany ? (
              <Center h="100%">
                <Loader />
              </Center>
            ) : (
              company && (
                <MatchedProfileView
                  company={company}
                  containerSize="fluid"
                  mobileCollabWorkspaceCard
                  questionnaireCta={
                    activeTab === "overview" &&
                      collaborationHasQuestionnaire &&
                      questionnaireMySubmitted !== null
                      ? questionnaireMySubmitted === false
                        ? { type: "start", onClick: handleGoToQuestionnaireTab }
                        : { type: "view", onClick: handleGoToQuestionnaireTab }
                      : null
                  }
                />
              )
            )}
          </Tabs.Panel>

          {/* Partner Readiness (questionnaire) — always mounted so the modal can show on page load */}
          <Tabs.Panel value="questionnaire" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {collaboration && userDetails?.company?.id && (
              <QuestionnaireTab
                matchId={id}
                collaboration={collaboration}
                userCompanyId={userDetails.company.id}
                autoOpenModal={autoOpenQuestionnaireModal}
                collaborationReadOnly={!!collaboration.collaborationReadOnly}
                onExitToCollaborationOverview={() => handleWorkspaceTabChange("overview")}
              />
            )}
          </Tabs.Panel>

          {/* Documents tab: shared files and NDA (mutual document between both companies) */}
          <Tabs.Panel value="documents" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {activeTab === "documents" && (
              <SharedDocumentsTab
                matchId={id}
                userCompanyName={userDetails?.company?.legalName}
                ndaOtherCompanyId={otherCompanyId}
                ndaCollaboration={collaboration}
                collaborationReadOnly={!!collaboration.collaborationReadOnly}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel
            value="chat"
            pt="md"
            style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}
          >
            <CollaborationChatTab
              matchId={id}
              chatOpen={collaboration.status === CollaborationStatus.BOTH_PAID}
              collaborationReadOnly={!!collaboration.collaborationReadOnly}
              currentUserId={userDetails?.id}
            />
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Box>
  );
}
